import { LingoError } from "./constants/lingoError";
import { RequireOnlyOne } from "./constants/tsHelpers";
import { ItemType, PortalItemType, PrivacyLevel } from "./constants/types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyObject = Record<string, any>;
export type Callback = () => void;
export type SuccessCallback = (success: boolean) => void;

export type ContentType = {
  aliases: string[];
  assetType: string;
  fileExtension: string;
  maxSize: number;
  replacements: string[];
  eligibleForDirectLinks: boolean;
  eligibleForCustomPreviews: boolean;
};

export type VersionedID = string;

// MARK : Custom Fields
// -------------------------------------------------------------------------------
export enum CustomFieldTypes {
  "text" = "text",
  "number" = "number",
  "date" = "date",
  "checklist" = "checklist",
  "select" = "select",
}

export enum CustomFieldOperation {
  "add" = "add",
  "remove" = "remove",
  "set" = "set",
}

export type CustomFieldOption = {
  id?: number; // Options created on the client do not yet have an ID value
  name: string;
  status: "active" | "deleted";
  selected?: boolean;
};

export type CustomField = {
  id: number;
  type: CustomFieldTypes;
  spaceId: number;
  name: string;
  status: "active" | "deleted";
  public: boolean;
  displayOrder: number;
  options?: CustomFieldOption[];
  value?: string | number;
};

// MARK : Tags
// -------------------------------------------------------------------------------
export type Tag = {
  count: number;
  value: string;
};

export type TagSuggestion = {
  value: string;
  type: string;
};

// MARK : User
// -------------------------------------------------------------------------------
export type User = {
  id: number;
  name: string;
  email: string;
  avatar: string;
  isActive: boolean;
  isAdmin?: boolean;
  createdAt?: string;
  emailPrefs?: {
    educational?: boolean;
    announcement?: boolean;
    newsletter?: boolean;
  };
  // spaces?: number[];
  mfa?: { totp_enabled?: boolean };
};

// MARK : Download Requests
// -------------------------------------------------------------------------------
export type DownloadRequestStatus = "pending" | "approved" | "denied";

export type DownloadRequest = {
  id: number;
  email: string;
  dateRequested: string;
  requestContext: string;
  status: DownloadRequestStatus;
  portalName?: string;
  kitName?: string | null;
  assetName?: string | null;
  assetId?: string | null;
  expiresAt?: string | null;
  approverName?: string | null;
};

// MARK : Theming
// -------------------------------------------------------------------------------
export enum SpaceThemeNames {
  classic = "classic",
  scher = "scher",
  wyman = "wyman",
}

export enum ThemeHeaderTitleOptions {
  name = "name",
  logo = "logo_only",
  none = "no_title",
}

export type SpaceLogoImage = {
  url: string;
  height: number;
  width: number;
};

export interface SpaceTheme {
  primaryColor: string;
  primaryColorDark: string;
  primaryColorLight: string;
  primaryColorTint: string;
  active: boolean;
  description?: string;
  headerBackgroundColor?: string;
  headerBackgroundImage?: string;
  themeName?: SpaceThemeNames;
  headerTitleStyle?: ThemeHeaderTitleOptions;
  headerTitleColor?: string;
  headerDescriptionColor?: string;
  headerLogoImage?: SpaceLogoImage & string;
  fonts?: {
    css: string;
    system: {
      family: string;
      source: string;
    };
  };
  noteSuccessColor?: string;
  noteInfoColor?: string;
  noteWarningColor?: string;
  noteNeutralColor?: string;
  assetLightColor?: string;
  assetDarkColor?: string;
}

// MARK : Space
// -------------------------------------------------------------------------------
export type SpaceMember = {
  id: number;
  spaceId: number;
  userId: number;
  user?: User;
  role: string;
  status: string;
  permissions: string[];
  dateAdded: string;
  dateUpdated: string;
  ssoEnabled?: boolean;
  notificationPrefs?: Record<string, boolean>;
  isEditor: boolean;

  // Optional per request
  joinedKits: number;
  kitAccess: KitMember[] | string[];
  portalAccess: PortalMember[] | string[];
};

export type PublicAccessApprovalMode =
  | "approve_all_automatically"
  | "approve_once_for_entity"
  | "approve_every_asset";

export type SpacePreview = {
  id: number;
  name: string;
  subdomain: string;
  domain: string;
  avatar: string;
  avatars: {
    avatar?: string;
    avatar16?: string;
    avatar32?: string;
    avatar180?: string;
    defaultAvatar: string;
  };
  theme: SpaceTheme;
  ssoOrganization?: { id: number; name: string; primarySpaceId: number };
  counts?: {
    users: number;
  };
  members?: {
    id: number;
    name: string;
    email: string;
    avatar: string;
    role: string;
  }[];
};

export type Space = {
  id: number;
  alert?: {
    id: string;
    style: "warning" | "alert";
    message: string;
  };
  name: string;
  subdomain: string;
  domain: string;
  avatar: string;
  avatars: {
    avatar?: string;
    avatar16?: string;
    avatar32?: string;
    avatar180?: string;
    defaultAvatar: string;
  };
  theme: SpaceTheme;
  members?: SpaceMember[];
  access?: {
    role: string;
    status: "active" | "revoked";
    permissions: string[];
    dateAdded: string;
    isPublic: boolean;
    upgradePermissions: { [key: string]: string };
    notificationPrefs?: Record<string, boolean>;
    ssoEnabled?: boolean;
  };
  counts: {
    assets: number;
    users: number;
    storage: number;
    invitations: number;
    editors: number;
  };
  status: string;
  features: string[];
  dateAdded: string;
  dateUpdated: string;
  joinSettings: SpaceJoinSettings;
  subscription?: SpaceSubscription;
  maxUsers: number;
  maxAssets: number;
  privacy: PrivacyLevel;
  dataPrivacy: string;
  indexable: boolean;
  ssoOrganization?: SSOOrganization;
  ssoPrivacy: string;
  downloadsRestricted: boolean;
  downloadsRestrictedApproveMode: PublicAccessApprovalMode;
  plan: SpacePlan;
};

export type Account = {
  charges?: AccountCharge[];
  card?: AccountCard;
  balance?: number;
};

export enum SpacePermission {
  view = "view",
  searchAssets = "search_assets",
  viewDashboard = "view_dashboard",
  viewMembers = "view_members",
  delete = "delete",
  manageBilling = "manage_billing",
  manageResources = "manage_resources",
  revokeInvitation = "revoke_invitation",
  removeUser = "remove_user",
  removeSelf = "remove_self",
  edit = "edit",
  createKit = "create_kit",
  inviteUser = "invite_user",
  changeUserRole = "change_user_role",
  viewAnalytics = "view_analytics",
  viewAdvancedAnalytics = "view_advanced_analytics",
  manageExports = "manage_exports",
  managePortals = "manage_portals",
  manageApiTokens = "manage_api_tokens",
  managePasswordAccess = "manage_password_access",
  managePublicAccess = "manage_public_access",
  manageSEO = "manage_seo",
  manageViews = "manage_views",
  manageMembers = "manage_members",
  viewAssetLibrary = "view_asset_library",
  createAsset = "create_asset",
  manageProducts = "manage_products",
  manageTags = "manage_tags",
  viewFields = "view_fields",
  editFields = "edit_fields",
  manageCustomTheme = "manage_custom_theme",
  manageCustomThemeFont = "manage_custom_theme_font",
  manageCustomThemeAppearance = "manage_custom_theme_appearance",
  manageCustomDomain = "manage_custom_domain",
  configureSSO = "configure_sso",
  downloadRequirements = "download_requirements",
  manageDownloadRequirements = "manage_download_requirements",
  createDynamicGalleryItems = "create_dynamic_gallery_items",
  manageDirectLinks = "manage_direct_links",
  viewDirectLinks = "view_direct_links",
}

export type AccountCharge = {
  id: number;
  amount: number;
  card: string;
  date: string;
  invoiceUrl: string;
  receiptExtra: string;
  refunded: boolean;
  status: "succeeded" | "failed" | "pending" | "refunded" | "canceled";
  stripeId: string;
};

export type AccountCard = {
  brand: string;
  country: string;
  expMonth: number;
  expYear: number;
  last4: string;
  receiptExtra: string;
  stripeId: string;
};

export type SpaceJoinSettings = {
  token?: string;
  joinWith: {
    role: string;
    portals?: { uuid: string }[];
    kits?: {
      uuid: string;
      role: string;
    }[];
  };
  allowedDomains?: string[];
};

export type SSOOrganization = {
  id: string;
  name: string;
  primarySpaceId: number;
};

export type SpaceSubscription = {
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string;
  customer: {
    stripeId?: string;
  };
  interval: string;
  plan: string;
  planName: string;
  price: number;
  pricePerUser?: number;
  totalPrice: number;
  quantity: number;
  status: "active" | "canceled" | "trialing" | "past_due";
  stripeId: string;
};

export type ChargePreview = {
  newCharges?: number;
  existingCredit: number;
  prorationDate: number;
  total: number;
};

export type PlanUsage = {
  included: number;
  quota?: number;
};

export type Plan = {
  type: "preset" | "custom";
  planIdentifier: "starter" | "business" | "enterprise";
  interval: "month" | "year";
  costPerEditor: number;
  featureGroup: string;
  planName: string;
  platformFee: number;
  trialDays: number;
  usage: {
    consumers: PlanUsage;
    dlBandwidth: PlanUsage;
    dlRequests: PlanUsage;
    editors: PlanUsage;
    storage: PlanUsage;
  };
};

export type SpacePlanUsage = {
  included: number;
  quota?: number;
  totalUsage: number;
  totalCost: number;
};

export type SpacePlan = {
  cancelAtPeriodEnd: boolean;
  costPerEditor: number;
  currentPeriodEnd: string;
  currentPeriodStart: string;
  interval: "month" | "year";
  planIdentifier: "business" | "starter" | "enterprise";
  planName: string;
  platformFee: number;
  status: "active" | "canceled" | "trialing" | "past_due";
  totalCost: number;
  type: string;
  usage: {
    consumers: SpacePlanUsage;
    dlBandwidth: SpacePlanUsage;
    dlRequests: SpacePlanUsage;
    editors: SpacePlanUsage;
    storage: SpacePlanUsage;
  };
  usageCurrentPeriodEnd: string;
  editorCount?: number;
  stripeId: string; // stripe ID, only provided in the fort.
};

export type APIToken = {
  name: string;
  token: string;
  writeAccess: boolean;
  spaceId: number;
  status: string;
  createdAt: string;
};

export type Invitation = {
  id: number;
  email: string;
  spaceId: number;
  type: string;
  joinWith: {
    role: string;
    portals?: { uuid: string }[];
    kits: { uuid: string; role: string }[];
  };
  note: string;
  token: string;
  invitedAt: string;
  acceptedAt: string;
  status: string;
  fromUserId: number;
};

export type Kit = {
  kitId: string;
  spaceId: number;
  urlId: string;
  access: KitAccess;
  portalAccess: boolean;
  recommendedVersion: number;
  description: string;
  name: string;
  dateAdded: string;
  dateUpdated: string;
  images: {
    cover?: string;
    [key: string]: unknown;
  };
  privacy: PrivacyLevel;
  portals?: Portal[];
  indexable: boolean;
  shortId: string;
  status: string;
  shareToken?: string;
  versions: string[];
  counts?: {
    members: number;
  };
  purchase?: {
    purchasedBy: number;
    purchasedAt: string;
    licenseName: string;
    licenseUrl: string;
  };
  publisher?: {
    name: string;
  };
  downloadsRestricted: boolean;
  downloadsRestrictedApproveMode: PublicAccessApprovalMode;
  theme?: SpaceTheme;
};

export enum KitPermission {
  view = "view",
  searchAssets = "search_assets",
  favorite = "favorite",
  changeOwnStatus = "change_own_status",
  viewAllContent = "view_all_content",
  edit = "edit",
  delete = "delete",
  changeMemberRole = "change_member_role",
  editContent = "edit_content",
  manage = "manage",
  manageExports = "manage_exports",
  manageCustomTheme = "manage_custom_theme",
  manageCustomThemeFont = "manage_custom_theme_font",
  manageCustomThemeAppearance = "manage_custom_theme_appearance",
  manageSharelinks = "manage_sharelinks",
  managePublicAccess = "manage_public_access",
  managePasswordAccess = "manage_password_access",
  manageSEO = "manage_seo",
  managePortalAccess = "manage_portal_access",
}

export type KitMember = {
  id: number;
  user: User;
  userId: number;
  spaceId: number;
  kitId: string;
  role: string;
  isFavorite?: boolean;
  kit?: Kit;
  status: "active" | "revoked";
};

export type KitAccess = {
  id: number;
  kitId: string;
  dateAdded: string;
  dateUpdated: string;
  isFavorite: boolean;
  permissions: string[];
  isPublic: boolean;
  useVersion?: number;
  role: string;
  spaceId: number;
  status: string;
  upgradePermissions: { [key: string]: string };
};

// MARK : Portals
// -------------------------------------------------------------------------------

export type Portal = {
  id: string;
  shortId: string;
  access: PortalAccess;
  space?: Space;
  spaceId: number;
  urlId: string;
  name: string;
  description: string;
  status: "active" | "deleted" | "pending";
  theme: SpaceTheme;
  subdomain?: string;
  domain?: string;
  counts: {
    items: 0;
    kits: 0;
    members: 0;
  };
  images: {
    cover: string;
    avatars: {
      avatar?: string;
      avatar16?: string;
      avatar32?: string;
      avatar180?: string;
      defaultAvatar: string;
    };
  };
  privacy: PrivacyLevel;
  indexable: boolean;
  downloadsRestricted: boolean;
  downloadsRestrictedApproveMode: PublicAccessApprovalMode;
  dateAdded: string;
  dateUpdated: string;
};

export type PortalPreview = {
  id: string;
  shortId: string;
  name: string;
  theme: SpaceTheme;
  spaceId: number;
  space: SpacePreview;
  images: Portal["images"];
  indexable: boolean;
  subdomain?: string;
  domain?: string;
};

export type PortalMigratePreview = {
  name: string;
  description: string;
  kits: Kit[];
  members: SpaceMember[];
};

export type PortalAccess = {
  id: number;
  permissions: string[];
  upgradePermissions: { [key: string]: string };
  isPublic: boolean;
  spaceRole?: string;
  role: string;
  status: string;
};

export enum PortalPermission {
  view = "view",
  searchAssets = "search_assets",
  edit = "edit",
  delete = "delete",
  editContent = "edit_content",
  manageCustomDomain = "manage_custom_domain",
  manageCustomTheme = "manage_custom_theme",
  manageCustomThemeFont = "manage_custom_theme_font",
  manageCustomThemeAppearance = "manage_custom_theme_appearance",
  managePublicAccess = "manage_public_access",
  managePasswordAccess = "manage_password_access",
  managePortalAccess = "manage_portal_access",
  manageDownloadRequirements = "manage_download_requirements",
  manageSEO = "manage_seo",
  viewMembers = "view_members",
}

export type PortalMember = {
  id: number;
  user: User;
  userId: number;
  portalId: string;
  permissions: string[];
  spaceRole?: string;
  role: string;
  portal?: Portal;
  status: "active" | "revoked";
  dateAdded: string;
  dateUpdated: string;
  spaceId: number;
};

export type PortalItem = {
  id: string;
  portalId: string;
  spaceId: number;
  displayOrder: number;
  type: PortalItemType;
  status: "active" | "deleted";
  dateAdded: string;
  dateUpdated: string;
  kitId: string;
  kit: Kit;
  asset?: Asset;
  assetId: string;
  data: {
    content?: string;
    backgroundColor: string;
  };
};

// MARK : Kit Content
// -------------------------------------------------------------------------------
export type KitVersion = {
  kitId: string;
  version: number;
  versionIdentifier: string;
  notes: string;
  creator?: User;
  dateAdded: string;
  dateUpdated: string;
  status: string;
  spaceId: number;
  sections: string[];
  counts: {
    assets: number;
    items: number;
    sections: number;
    trash: number;
    recovery: number;
  };
};

export type Section = {
  id: string;
  shortId: string;
  kitId: string;
  urlId: string;
  displayOrder: number;
  status: "active" | "deleted" | "pending";
  name: string;
  version: number;
  kit?: Kit;
  items?: VersionedID[];
  // assets is compiled by the reducer and used to power the next/prev
  // buttons in asset detail view
  assets?: string[];
  headers?: HeadingItem[];
  counts: {
    items: number;
  };
  isSeparator: boolean;
};

export type HeadingItem = {
  id: string;
  shortId: string;
  name: string;
  version: number;
  displayOrder: number;
};

export type ItemDisplayProperties = {
  imageAlignment?: "leading" | "top" | "trailing";
  showMetadata?: boolean;
  allowPreview?: boolean;
  allowDownload?: boolean;
  caption?: string;
  cardBackgroundColor?: string;
  size?: 1 | 2 | 3 | 4 | 5 | 6;
  autoplay?: boolean;
  displayStyle?: "image" | "text_only";
};

export type Item = {
  id: string;
  shortId: string;
  sectionId?: string;
  itemId?: string;
  assetId?: string;
  kitId: string;
  spaceId: number;
  urlId: string;

  space?: Space;
  kit?: Kit;
  kitVersion?: KitVersion;
  section?: Section;
  item?: Item;
  asset?: Asset;
  type: ItemType;
  status: "active" | "trashed" | "deleted";

  version: number;
  displayOrder: number;
  dateAdded: string;
  dateUpdated: string;
  items?: VersionedID[];
  // assets is compiled by the reducer and used to power the next/prev
  // buttons in asset detail view
  assets?: string[];
  displayProperties?: ItemDisplayProperties;
  data: {
    displayStyle?: "square" | "full_width" | "image" | "text_only";
    layout?: "image_above" | "image_left" | "image_right";
    displaySize?: number;
    content?: string;
    codeLanguage?: string;
    backgroundColor: string;
    color?: string; // guides
    title?: string; // guides
    noteTextColor?: "black" | "white";

    // Galleries
    dateRefreshed?: string;
    viewName?: string;
    name?: string;
    viewId?: number;
    assets?: number;
    itemProcessing?: "pending" | "complete";
  };
};

export type AssetProcesssingDetails = {
  code: number;
  message: string;
};

export type Color = {
  name?: string;
  hue: number;
  saturation: number;
  brightness: number;
  alpha: number;
  coverage: number;
  cmyk?: string;
  pms?: string;

  // Added by entity formatters
  hex: string;
  hex8: string;
  hsb: string;
  rgb: string;
};

export type AssetFont = {
  displayName: string;
  name: string;
  family: string;
  variant: string;
  googleVariant?: string;
  extension: string;
  source: string;
  stylesheetUrl: string;
};

export type AssetCustomFields = {
  [key: string]: string[] | string | number;
};

export type Asset = {
  id: string;
  sourceId?: string;
  spaceId: number;
  category: string;
  shareToken: string;
  type: string;
  permalink: string;
  dimensions: string; // WxH
  width?: number;
  height?: number;
  name: string;
  uploaderId: number;
  uploader: User;
  dateAdded: string;
  dateUpdated: string;
  size: number;
  keywords: string;
  autoKeywords?: string;
  notes: string;
  isFont: boolean;
  fileId: string;
  fileHash: string;
  fileUpdated: string;
  meta?: {
    assetProcessingDetails?: AssetProcesssingDetails;
    assetProcessing: "processing" | "complete" | "error";
    content?: {
      url?: string;
      description?: string;
      title?: string;
      site_name?: string;
      favicon?: string;
      fld?: string;
    };
    preview?: {
      dimensions: string;
      file_type: string;
      id: string;
    };
    duration?: number;
    filecuts?: {
      availableTypes: { enabled: boolean; resizable: boolean; set_dpi?: boolean; type: string }[];
      presets: { description: string; size: string; type: string; dpi?: number }[];
    };
    figma?: {
      url: string;
    };
    font?: AssetFont;
    textStyle?: AnyObject;
    sketchStyleAttributes?: AnyObject;
    backgroundColor: string;
  };
  thumbnails?: {
    1232: string;
    480: string;
    292: string;
    24: string;
  };
  thumbnailPlaceholders?: {
    1232: string;
    480: string;
    292: string;
  };
  colors?: Color[];
  items?: Item[] | string[];
  fields: AssetCustomFields;
  versionedFields?: CustomField[];
};

export type DirectLink = {
  id: number;
  assetId: string;
  spaceId: number;
  name: string;
  url: string;
  dateAdded: string;
  dateUpdated: string;
};

// MARK : API Responses
// -------------------------------------------------------------------------------
export type LingoApiResponse = {
  type: string;
  statusCode?: number;
  error?: LingoError;
  response?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result?: any;
    entities?: Entities;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  // result: any;
  // [key: string]: any;
};

type Entities = Partial<{
  spaces: Record<string, Space>;
  spaceMembers: Record<string, SpaceMember>;
  users: Record<string, User>;
  invitations: Record<string, Invitation>;
  apiTokens: Record<string, APIToken>;
  portalItems: Record<string, PortalItem>;
  portalMembers: Record<string, PortalMember>;
  portals: Record<string, Portal>;
  kits: Record<string, Kit>;
  kitVersions: Record<string, KitVersion>;
  kitMembers: Record<string, KitMember>;
  sections: Record<string, Section>;
  items: Record<string, Item>;
  assets: Record<string, Asset>;
  fields: Record<string, CustomField>;
  views: Record<string, AssetView>;
  analytics: Record<string, AnyObject>;
  downloadRequests: Record<string, DownloadRequest>;
  assetFilters: Record<string, AnyObject>;
  directLinks: Record<string, DirectLink>;
  productTypes: Record<string, ProductType>;
  productFields: Record<string, ProductField>;
  markets: Record<string, Market>;
  products: Record<string, Product>;
  variants: Record<string, Variant>;
  presets: Record<string, Preset>;
}>;

export type APIResult<R> = {
  success: boolean;
} & RequireOnlyOne<{ error: LingoError; result: R }>;
/**
 * Typically used for sync or batch endpoints where the result is a list of change results
 *
 * For example, batch updating assets could look like
 * ```
 * APIResultList<"assets">
 * {
 *  entities: {},
 *  result: {
 *    assets: [ // <- the key provided
 *      // The asset is normalized in entities
 *      { "success": true, "result": "asset-uuid" },
 *      { "success": false, "error": {
 *         "code": 400, "message": "Invalid asset" }
 *      }
 *   ]
 *  }
 * }
 * ```
 */

export type APIResultList<K extends string, R> = Record<K, APIResult<R>[]>;

export type APIResponse<R = unknown> = {
  entities?: Entities;
  result: R;
};

// MARK : Search
// -------------------------------------------------------------------------------
export enum SearchFilterTypes {
  "kit" = "kit",
  "type" = "type",
  "orientation" = "orientation",
  "tag" = "tag",
  "after" = "after",
  "before" = "before",
  "name" = "name",
  "uploader" = "uploader",
  "date" = "date",
  "text" = "text",
  "token" = "token",
  "keyword" = "keyword",
  "suggestion" = "suggestion",
  "used-in" = "used-in",
  // custom field filter types
  "field-text" = "field-text",
  "field-date" = "field-date",
  "field-number" = "field-number",
  "field-checklist" = "field-checklist",
  "field-select" = "field-select",
}

export type OperatorValues = {
  eq?: string;
  gt?: string;
  lt?: string;
  gte?: string;
  lte?: string;
};

export type SearchFilterInput = {
  type: SearchFilterTypes;
  filter_type?: string;
  id?: string;
  value?: string;
  operator_values?: OperatorValues;
  display?: string;
  kit_uuid?: string;
  image?: string;
  avatar?: string;
  field_id?: number;
  user_id?: number;
  version?: number;
  style?: string;
  score?: number;
  offset?: number;
  name?: string;
  length?: number;
  // Jump to filters
  object_type?: "kit" | "section" | "item" | "gallery";
  kit?: {
    kit_uuid: string;
    short_id: string;
    name: string;
    privacy: PrivacyLevel;
  };
  section?: {
    kit_uuid: string;
    short_id: string;
    name: string;
    version: number;
  };
  item?: {
    uuid: string;
    short_id: string;
    version: number;
    type: string;
  };
};

export type AssetView = {
  id: number;
  name: string;
  spaceId: number;
  filters: SearchFilterInput[];
  status: "active" | "deleted";
};

export enum SearchOperators {
  "eq" = "eq",
  "lt" = "lt",
  "gt" = "gt",
  "lte" = "lte",
  "gte" = "gte",
}

// MARK : Uploads
// -------------------------------------------------------------------------------

export type Upload = {
  id: string;
  name: string;
  type: string;
  status: "started" | "success" | "failed";
  spaceId?: number;
  size?: number;
  kitId?: string;
  isAsset?: boolean;
  progress?: number;
  dateAdded?: Date;
  error?: {
    message: string;
  };
};

// MARK : Products
// -------------------------------------------------------------------------------

export type FieldType =
  | "text"
  | "number"
  | "select"
  | "multiselect"
  | "date"
  | "boolean"
  | "name"
  | "description"
  | "sku"
  | "price"
  | "asset"
  | "images"
  | "cannabis_subcategory"
  | "phenotype"
  | "taste"
  | "mood"
  | "ingredients"
  | "terpenes";

export type FieldSelectOption = {
  uuid?: string;
  value: string;
  label: string;
  displayOrder?: number;
};

export type CreateFieldInput = {
  name: string;
  fieldType: FieldType;
  identifier?: string;
  required?: boolean;
  displayOrder?: number | "append";
  options?: FieldSelectOption[];
  config?: AnyObject;
};

export type UpdateFieldInput = {
  /** Existing field UUID — include to update, omit to create. */
  uuid?: string;
  name?: string;
  fieldType?: FieldType;
  identifier?: string;
  required?: boolean;
  displayOrder?: number;
  options?: FieldSelectOption[];
  config?: AnyObject;
};

export type TextConfig = {
  multiline?: boolean;
};

export type NumberConfig = {
  numberType?: "default" | "currency" | "percentage";
  decimals?: number;
  thousandsSeparator?: boolean;
  currency?: string;
};

export type PriceConfig = {
  currency?: string;
  decimals?: number;
  thousandsSeparator?: boolean;
};

export type DateConfig = {
  dateFormat?: string;
  includeTime?: boolean;
  timeFormat?: "24h" | "12h";
};

export type CannabisConfig = {
  parentCategory?: string;
};

export type AssetFieldConfig = {
  maxAssets?: number;
  assetTypes?: string[];
};

export type ProductFieldConfig = TextConfig &
  NumberConfig &
  PriceConfig &
  DateConfig &
  CannabisConfig &
  AssetFieldConfig;

export type ProductField = {
  id: number;
  uuid: string;
  productTypeUuid: string;
  name: string;
  identifier: string;
  fieldType: FieldType;
  required: boolean;
  status: string;
  displayOrder: number;
  options?: FieldSelectOption[];
  config?: ProductFieldConfig;
  creatorId: number;
  dateAdded: string;
  dateUpdated: string;
};

// MARK : PIM Field Value envelopes
// -------------------------------------------------------------------------------

export type FieldSource = null | "override" | "inherited";

export type PimOptionRef = {
  uuid?: string;
  value: string;
  label?: string;
};

export type PimAssetEntry = {
  assetUuid: string;
  displayOrder?: number;
  inherited?: boolean;
  asset?: Asset;
};

export type PimScalarValue = {
  value: string | number | boolean | null;
  source: FieldSource;
};

export type PimOptionValue = {
  option: PimOptionRef | null;
  source: FieldSource;
};

export type PimMultiOptionValue = {
  options: PimOptionRef[];
  source: FieldSource;
};

export type PimAssetsValue = {
  assets: PimAssetEntry[];
  source: FieldSource;
};

export type PimFieldValue = PimScalarValue | PimOptionValue | PimMultiOptionValue | PimAssetsValue;

export type PimFields = Record<string, PimFieldValue>;

export type ProductType = {
  id: number;
  uuid: string;
  spaceId: number;
  name: string;
  counts: {
    fields: number;
    products: number;
  };
  description: string;
  status: string;
  displayOrder: number;
  creatorId: number;
  dateAdded: string;
  dateUpdated: string;
  fields: ProductField[];
};

// MARK : Markets
// -------------------------------------------------------------------------------

export type Market = {
  id: number;
  uuid: string;
  spaceId: number;
  name: string;
  notes: string;
  status: string;
  displayOrder: number;
  creatorId: number;
  dateAdded: string;
  dateUpdated: string;
  counts: {
    products: number;
  };
};

// MARK : Products
// -------------------------------------------------------------------------------

export type Product = {
  id: number;
  uuid: string;
  name: string;
  spaceId: number;
  productTypeUuid: string;
  productType?: ProductType;
  status: string;
  fields: PimFields;
  marketUuid: string | null;
  counts: {
    variants: number;
    activeVariants: number;
    options: number;
    markets: number;
  };
  variants?: Variant[];
  previewAsset?: Asset | null;
  creatorId: number;
  dateAdded: string;
  dateUpdated: string;
};

// MARK : Variants
// -------------------------------------------------------------------------------

export type Variant = {
  id: number;
  uuid: string;
  spaceId: number;
  productUuid: string;
  status: string;
  optionValues: { [optionName: string]: string };
  fields: PimFields;
  marketUuid: string | null;
  computedMarketUuid: string | null;
  displayOrder: number;
  creatorId: number;
  dateAdded: string;
  dateUpdated: string;
};

// MARK : Presets
// -------------------------------------------------------------------------------

export type PresetValue = {
  value: string;
  label: string;
};

export type Preset = {
  key: string;
  name: string;
  type: string;
  description: string;
  values: PresetValue[];
};
